import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { getStoredUser } from '@/hooks/useAuth';
import { OwnerStation } from '@/mocks/ownerDashboard';
import { useOwnerDashboard } from '@/hooks/useOwnerDashboard';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import DispatcherKpiRow from './components/DispatcherKpiRow';
import GenerationChart from './components/GenerationChart';
import MapSection from '@/pages/dashboard/owner/components/MapAndRevenue';
import PortfolioTilesSection from '@/pages/dashboard/owner/components/PortfolioTilesSection';
import OperationsSection from '@/pages/dashboard/owner/components/OperationsSection';
import InverterHealth from '@/pages/dashboard/owner/components/InverterHealth';

// Dispatcher dashboard — same shape as the owner dashboard but TECHNICAL only:
// generation, plant status, forecast, inverters, incidents. No money / markets.
// Reuses the portfolio data hook and the finance-free owner widgets (hideFinance).
export default function DispatcherDashboardPage() {
  const { isDark } = useTheme();
  const { t, i18n } = useTranslation();
  const user = getStoredUser();
  const navigate = useNavigate();
  const { setSelectedAsset } = useSelectedAsset();
  const { stations, incidents, kpis, lostRevenue, loading, isFallback } = useOwnerDashboard();

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';

  const _dateLocale = (i18n.language || 'uk').toLowerCase().startsWith('pl') ? 'pl-PL' : (i18n.language || 'uk').toLowerCase().startsWith('en') ? 'en-US' : 'uk-UA';
  const _today = new Date().toLocaleDateString(_dateLocale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const todayLabel = _today.charAt(0).toUpperCase() + _today.slice(1);
  const _hour = new Date().getHours();
  const greeting =
    _hour >= 5 && _hour < 12 ? t('greeting_morning')
    : _hour >= 12 && _hour < 18 ? t('greeting_day')
    : _hour >= 18 && _hour < 23 ? t('greeting_evening')
    : t('greeting_night');
  const _firstName = (user?.displayName ?? '').split(' ')[0];
  const dispName = /ій$/.test(_firstName) ? _firstName.slice(0, -1) + 'ю' : _firstName;

  const selectAsset = (s: OwnerStation) =>
    setSelectedAsset({
      id: s.id,
      assetName: s.name,
      installedCapacityMw: s.installedMw,
      region: s.region,
      operationalStatus: s.status,
    });
  const openAssetTechnical = (s: OwnerStation) => { selectAsset(s); navigate(`/assets/${s.id}?tab=technical`); };
  const openAssetForecast = (s: OwnerStation) => { selectAsset(s); navigate('/forecast'); };

  return (
    <DashboardLayout breadcrumb={['Дашборд диспетчера']}>
      <div className="max-w-full" style={{ color: textMain }}>
        {/* Page header */}
        <div className="mb-5">
          <h1 className="text-xl font-bold mb-1" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {greeting}{dispName ? `, ${dispName}` : ''} 👋
          </h1>
          <p className="text-sm" style={{ color: textMuted, fontFamily: 'Inter, sans-serif' }}>
            <span style={{ color: textMain, fontWeight: 600 }}>{todayLabel}</span>
            {' · '}{t('d_disp_console')} · {stations.length || 6} {t('d_ses_plural')}
            {isFallback && (
              <span style={{ color: '#F59E0B' }}> · {t('d_demo_data')}</span>
            )}
          </p>
        </div>

        {/* Technical KPI row */}
        <DispatcherKpiRow
          kpis={kpis}
          stations={stations}
          loading={loading}
          onStationDetail={openAssetTechnical}
          onStationForecast={openAssetForecast}
        />

        {/* Generation plan vs fact (MWh) */}
        <GenerationChart />

        {/* Map + technical station list */}
        <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-4 mb-5">
          <MapSection
            stations={stations}
            onStationClick={openAssetTechnical}
            onStationForecast={openAssetForecast}
            hideFinance
          />
          <PortfolioTilesSection stations={stations} onStationClick={openAssetTechnical} compact hideFinance />
        </div>

        {/* Incidents (technical) */}
        <OperationsSection incidents={incidents} lostRevenue={lostRevenue} hideFinance />

        {/* Inverter health */}
        <InverterHealth />
      </div>
    </DashboardLayout>
  );
}
